import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { axiosCacheClient } from '@/services/axios-cache-client'
import { decompressPeople, decompressJson } from '@/utils/Utils'
import { logger } from '@/utils/logger'

export const getMeta = async ({ boardId }) => {
  try {
    const url = new URL(`${await API_URL()}/meta/${boardId}`)

    const ret = await axiosCacheClient.get(url, {
      responseType: 'arraybuffer',
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      const data = decompressPeople(ret.data) || {}
      return data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return null
  }
}

export const getPlansApprovalMeta = async ({ boardId, boardIds }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/meta/${boardId}/plans-approval`,
      { data: { boardIds } },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return {}
  }
}

export const getOrgApprovedRoles = async ({ boardId }) => {
  try {
    const ret = await axios.get(`${await API_URL()}/meta/${boardId}/org-approved-roles`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return []
  }
}

export const getPlanApprovedRoles = async ({ boardId }) => {
  try {
    const ret = await axiosCacheClient.get(`${await API_URL()}/meta/${boardId}/approved-roles`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return []
  }
}

export const getPlanExcludedRoles = async ({ boardId }) => {
  try {
    const ret = await axios.get(`${await API_URL()}/meta/${boardId}/excluded-roles`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return []
  }
}

export const getPlanSubmittedRoles = async ({ boardId }) => {
  try {
    const ret = await axios.get(`${await API_URL()}/meta/${boardId}/submitted-roles`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return []
  }
}

export const getPlanRolesInReview = async ({ boardId }) => {
  try {
    const ret = await axios.get(`${await API_URL()}/meta/${boardId}/roles-in-review`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return []
  }
}

export const getPlanDatesRange = async ({ boardId }) => {
  try {
    const ret = await axios.get(`${await API_URL()}/meta/${boardId}/roles-date-range`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return []
  }
}

/**
 * Fetches the list of categories for the org.
 */
export const fetchHubCategories = async ({ boardId, includeCustomFieldTypes = [] }) => {
  try {
    const url = new URL(`${await API_URL()}/meta/${boardId}/hub-categories`)
    const normalizedTypes = [...new Set(includeCustomFieldTypes)]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
    if (normalizedTypes.length) {
      url.searchParams.set('includeCustomFieldTypes', normalizedTypes.join(','))
    }

    const ret = await axios.get(url, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return null
  }
}

/**
 * For given personIds, calculate the count of occurrences of each value of the given custom fields in the subtree of each personId
 * @param boardId
 * @param personIds - people currently visible in the org chart
 * @param fields - format: { fieldId, data }
 * @param filters - filters applied when counting within each subtree
 */
export const countByCustomFields = async ({ boardId, personIds, fields, filters }) => {
  if (!boardId) return {}
  if (!personIds || personIds.length === 0) return {}
  if (!fields || fields.length === 0) return {}

  const ret = await axios.post(
    `${await API_URL()}/meta/${boardId}/count-by-custom-field`,
    {
      data: {
        personIds,
        fields,
        filters
      }
    },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

/**
 * For given orgUnitIds, calculate the count of occurrences of each value of the given custom fields in the subtree of each orgUnitId
 * @param boardId
 * @param orgUnitIds
 * @param fields - format: { fieldId, data }
 */
export const countByCustomFieldsOrgUnits = async ({ boardId, orgUnitIds, fields, filters }) => {
  if (!boardId) return {}
  if (!orgUnitIds || orgUnitIds.length === 0) return {}
  if (!fields || fields.length === 0) return {}

  const ret = await axios.post(
    `${await API_URL()}/meta/${boardId}/count-by-custom-field-org-units`,
    {
      data: {
        orgUnitIds,
        fields,
        filters
      }
    },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

/**
 * For given personIds, calculate the count of occurrences of each value of the given custom fields in the subtree of each personId
 * @param boardId
 * @param personIds
 * @param fields - format: { fieldId, data }
 */
export const countByPersonAttribute = async ({ boardId, personIds, attribute }) => {
  if (!boardId) return {}
  if (!personIds || personIds.length === 0) return {}

  const ret = await axios.post(
    `${await API_URL()}/meta/${boardId}/count-by-attribute`,
    {
      data: {
        personIds,
        attribute
      }
    },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function getMetrics({
  boardId,
  includeApprovedRoles = false,
  collapseOn = null,
  metricIds = null
}) {
  const headers = await getAuthHeader()
  const data = { includeApprovedRoles, collapseOn }

  if (Array.isArray(metricIds)) {
    data.metricIds = metricIds
  }

  const ret = await axios.post(
    `${await API_URL()}/meta/${boardId}/metrics`,
    { data },
    {
      responseType: 'arraybuffer',
      headers
    }
  )

  if (ret.status === 200) {
    const data = decompressPeople(ret.data) || []
    return data
  } else throw new Error(ret.statusText)
}

let runningPromise = null

/**
 * Fetches the list of vacant subordinates for a given board ID.
 * @param {string} boardId - The ID of the board.
 * @returns {Promise<Array>} - A promise that resolves to an array of vacant subordinates.
 */
export const vacantSubordinates = async ({ boardId }) => {
  try {
    const url = new URL(`${await API_URL()}/meta/${boardId}/vacant-subordinates`)

    const ret = await axios.get(url, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error.message)
    return []
  }
}

/**
 * Retrieves the count of vacant subordinates for a given board ID.
 * @param {string} boardId - The ID of the board.
 * @returns {Promise<number|null>} - A promise that resolves to the count of vacant subordinates, or null if an error occurs.
 */
export const countVacantSubordinates = async ({ boardId, filters }) => {
  // If there's already a request running, wait for it and return its result
  if (runningPromise) {
    return runningPromise
  }

  // Start a new request
  runningPromise = (async () => {
    try {
      const ret = await axios.post(
        `${await API_URL()}/meta/${boardId}/count-vacant-subordinates`,
        { data: { filters } },
        {
          headers: { ...(await getAuthHeader()) }
        }
      )

      if (ret.status === 200) {
        return ret.data
      } else {
        throw new Error(ret.statusText)
      }
    } catch (error) {
      logger.info(error.message)
      return null
    } finally {
      // Clear the running promise when done, regardless of success or failure
      runningPromise = null
    }
  })()

  return runningPromise
}

export const getDynamicFilters = async ({ boardId, filters }) => {
  const ret = await axios.post(
    `${await API_URL()}/meta/${boardId}/dynamic-filters`,
    { data: { filters } },
    {
      headers: { ...(await getAuthHeader()) }
    }
  )
  if (ret.status === 200) {
    return decompressJson(ret.data) || []
  }
}
